import { createElement, useMemo, useReducer, useRef, useState } from "react";
import {
  Handle,
  Position,
  useStore as useReactFlowStore,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
} from "@xyflow/react";
import { Layers, Router, Server, Wifi } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/cn";
import { TopologyFlowCanvas } from "@/components/app/topology/topology-flow-canvas";

export type TopologyExplorerStatus =
  | "online"
  | "alerting"
  | "offline"
  | "unknown";

const STATUS_ORDER: TopologyExplorerStatus[] = [
  "online",
  "alerting",
  "offline",
  "unknown",
];

interface ExplorerDevice {
  serial?: string;
  name?: string;
  model?: string;
  vendor?: string;
  status?: string;
  ipAddress?: string | null;
  mac?: string | null;
  tags?: string[];
}

interface ExplorerEdgeData {
  sourcePort?: string | null;
  targetPort?: string | null;
}

export interface TopologyExplorerPort {
  portId: string | null;
  name?: string | null;
  status?: string | null;
  speed?: string | null;
  clientCount?: number | null;
  vlan?: string | number | null;
}

export interface TopologyExplorerClient {
  id?: string | null;
  description?: string | null;
  mac?: string | null;
  ip?: string | null;
  ssid?: string | null;
  switchport?: string | null;
  vlan?: number | string | null;
  manufacturer?: string | null;
}

export interface TopologyExplorerNodeDetails {
  serial: string;
  name?: string | null;
  model?: string | null;
  productType: "switch" | "wireless" | "appliance" | "other";
  ports?: TopologyExplorerPort[];
  clients?: TopologyExplorerClient[];
}

interface HoverCardState {
  nodeId: string;
  x: number;
  y: number;
}

function iconForDevice(device: ExplorerDevice): typeof Wifi {
  const m = (device.model ?? "").toUpperCase();
  if (
    m.startsWith("MR") ||
    m.startsWith("CW") ||
    m.startsWith("AP") ||
    m.startsWith("IAP")
  ) {
    return Wifi;
  }
  if (m.startsWith("MS") || m.includes("CX")) return Layers;
  if (
    m.startsWith("MX") ||
    m.startsWith("Z") ||
    m.startsWith("9") ||
    m.startsWith("72")
  ) {
    return Router;
  }
  return Server;
}

function inferIsSwitch(device: ExplorerDevice, label: string): boolean {
  const m = (device.model ?? "").toUpperCase();
  if (m.startsWith("MS") || m.includes("CX")) return true;
  const lower = label.toLowerCase();
  return lower.includes("switch");
}

function inferIsAccessPoint(device: ExplorerDevice, label: string): boolean {
  const m = (device.model ?? "").toUpperCase();
  if (
    m.startsWith("MR") ||
    m.startsWith("CW") ||
    m.startsWith("AP") ||
    m.startsWith("IAP")
  ) {
    return true;
  }
  const lower = label.toLowerCase();
  return lower.includes(" ap") || lower.includes("access point");
}

function normalizeDevice(node: Node): ExplorerDevice {
  const data =
    (node.data as { device?: ExplorerDevice; label?: string } | undefined) ??
    {};
  if (data.device) {
    return {
      ...data.device,
      serial: data.device.serial ?? node.id,
      name: data.device.name ?? data.label ?? node.id,
      model: data.device.model ?? "—",
      status: normalizeStatus(data.device.status),
    };
  }
  const label = data.label ?? node.id;
  return {
    serial: node.id,
    name: label,
    model: "—",
    status: normalizeStatus(undefined),
    vendor: "unknown",
    ipAddress: null,
    mac: null,
  };
}

function ExplorerNode({ data }: NodeProps<Node<{ device?: ExplorerDevice }>>) {
  const device = (data.device ?? {}) as ExplorerDevice;
  const status = normalizeStatus(device.status);
  const zoom = useReactFlowStore((s) => s.transform[2]);
  const dense = zoom < 0.5;

  if (dense) {
    return (
      <div
        className={cn(
          "relative h-3 w-3 rounded-full ring-2 ring-bg-0/60",
          status === "online" && "bg-mint",
          status === "alerting" && "bg-peach",
          status === "offline" && "bg-rose",
          status === "unknown" && "bg-fg-mute",
        )}
      >
        <Handle
          type="target"
          position={Position.Left}
          className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
        />
        <Handle
          type="source"
          position={Position.Right}
          className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative min-w-[150px] cursor-grab active:cursor-grabbing rounded-xl border bg-surface px-3 py-2.5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] hover:ring-2 hover:ring-violet/40",
        status === "online" && "border-mint/40",
        status === "alerting" && "border-peach/40",
        status === "offline" && "border-rose/40",
        status === "unknown" && "border-line",
      )}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
      />
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-md",
            status === "online" && "bg-mint/15 text-mint",
            status === "alerting" && "bg-peach/15 text-peach",
            status === "offline" && "bg-rose/15 text-rose",
            status === "unknown" && "bg-line/30 text-fg-mute",
          )}
        >
          {createElement(iconForDevice(device), { size: 12 })}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-semibold text-fg">
            {device.name ?? "(unnamed)"}
          </div>
          <div className="font-mono text-[9.5px] text-fg-mute">
            {device.model ?? "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

const NODE_TYPES: NodeTypes = { default: ExplorerNode };

function nodeLabel(node: Node): string {
  const data =
    (node.data as { label?: string; name?: string } | undefined) ?? {};
  return data.label ?? data.name ?? node.id;
}

function normalizeStatus(
  value: string | null | undefined,
): TopologyExplorerStatus {
  const v = (value ?? "").toLowerCase().trim();
  if (v === "online") return "online";
  if (v === "alerting") return "alerting";
  if (v === "offline") return "offline";
  return "unknown";
}

function TopologyFilterOverlay({
  filterLabel,
  enabledStatuses,
  setEnabledStatuses,
  isAccessPointNode,
  accessPointCount,
  showAccessPoints,
  setShowAccessPoints,
}: {
  filterLabel: string;
  enabledStatuses: Set<TopologyExplorerStatus>;
  setEnabledStatuses: (next: Set<TopologyExplorerStatus>) => void;
  isAccessPointNode: ((node: Node) => boolean) | undefined;
  accessPointCount: number;
  showAccessPoints: boolean;
  setShowAccessPoints: (next: boolean) => void;
}) {
  return (
    <div className="pointer-events-auto absolute right-3 top-3 z-20 w-[245px] rounded-xl border border-line/85 bg-bg-0/92 p-2.5 shadow-[0_12px_28px_-20px_rgba(0,0,0,0.8)] backdrop-blur">
      <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.12em] text-fg-mute">
        {filterLabel}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {STATUS_ORDER.map((status) => {
          const active = enabledStatuses.has(status);
          return (
            <button
              key={status}
              type="button"
              onClick={() => {
                const next = new Set(enabledStatuses);
                if (next.has(status)) next.delete(status);
                else next.add(status);
                setEnabledStatuses(next);
              }}
              className={cn(
                "inline-flex items-center justify-center rounded-md border px-2 py-1 text-[11px] font-semibold capitalize transition-colors",
                active
                  ? status === "online"
                    ? "border-mint/50 bg-mint/12 text-mint"
                    : status === "alerting"
                      ? "border-peach/50 bg-peach/12 text-peach"
                      : status === "offline"
                        ? "border-rose/50 bg-rose/12 text-rose"
                        : "border-violet/45 bg-aurora-soft text-violet"
                  : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
              )}
            >
              {status}
            </button>
          );
        })}
      </div>
      {isAccessPointNode && accessPointCount > 0 ? (
        <button
          type="button"
          onClick={() => setShowAccessPoints(!showAccessPoints)}
          className={cn(
            "mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-colors",
            showAccessPoints
              ? "border-violet/45 bg-aurora-soft text-violet"
              : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
          )}
        >
          <Wifi size={12} />
          Access Points
        </button>
      ) : null}
    </div>
  );
}

function TopologyHoverCard({
  hoverCard,
  hoverDevice,
}: {
  hoverCard: HoverCardState;
  hoverDevice: ExplorerDevice;
}) {
  return (
    <div
      className="pointer-events-none absolute z-30 w-[300px] rounded-xl border border-line/90 bg-bg-0/95 p-3 shadow-[0_20px_36px_-24px_rgba(0,0,0,0.9)] backdrop-blur"
      style={{ left: hoverCard.x, top: hoverCard.y }}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-semibold text-fg">
            {hoverDevice.name ?? "(unnamed device)"}
          </div>
          <div className="truncate font-mono text-[10px] text-fg-mute">
            {hoverDevice.serial ?? "—"}
          </div>
        </div>
        <Badge
          size="sm"
          variant={
            normalizeStatus(hoverDevice.status) === "online"
              ? "ok"
              : normalizeStatus(hoverDevice.status) === "alerting"
                ? "warn"
                : normalizeStatus(hoverDevice.status) === "offline"
                  ? "err"
                  : "neutral"
          }
          dot
        >
          {normalizeStatus(hoverDevice.status)}
        </Badge>
      </div>
      <div className="grid grid-cols-1 gap-1.5 text-[10.5px] text-fg-dim">
        <HoverDetailRow label="Model" value={hoverDevice.model ?? "—"} mono />
        <HoverDetailRow label="Vendor" value={hoverDevice.vendor ?? "—"} />
        <HoverDetailRow label="IP" value={hoverDevice.ipAddress ?? "—"} mono />
        <HoverDetailRow label="MAC" value={hoverDevice.mac ?? "—"} mono />
        <HoverDetailRow
          label="Tags"
          value={hoverDevice.tags?.length ? hoverDevice.tags.join(", ") : "—"}
        />
      </div>
    </div>
  );
}

function NodeDetailsRenderer({
  fn,
  node,
}: {
  fn: (node: Node) => React.ReactNode;
  node: Node;
}) {
  return <>{fn(node)}</>;
}

function TopologyDetailsPanel({
  selectedNode,
  renderNodeDetails,
  onRefreshSelectedNode,
  onClose,
  selectedNodeDetailsLoading,
  selectedNodeDetailsError,
  selectedNodeDetails,
  getNodeStatus,
}: {
  selectedNode: Node | null;
  renderNodeDetails?: (node: Node) => React.ReactNode;
  onRefreshSelectedNode?: (node: Node) => void;
  onClose: () => void;
  selectedNodeDetailsLoading: boolean;
  selectedNodeDetailsError: string | undefined;
  selectedNodeDetails: TopologyExplorerNodeDetails | undefined;
  getNodeStatus?: (node: Node) => string | null | undefined;
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
          Details
        </span>
        <div className="flex items-center gap-1.5">
          {selectedNode ? (
            <>
              {onRefreshSelectedNode ? (
                <button
                  type="button"
                  onClick={() => onRefreshSelectedNode(selectedNode)}
                  className="inline-flex h-6 items-center rounded-md border border-line px-2 text-[10px] font-semibold text-fg-dim transition-colors hover:bg-surface-2 hover:text-fg"
                >
                  Refresh
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close node details"
                title="Close details"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-line text-fg-mute transition-colors hover:bg-surface-2 hover:text-fg"
              >
                ✕
              </button>
              <Badge variant="neutral" size="sm">
                {selectedNode.id}
              </Badge>
            </>
          ) : null}
        </div>
      </div>

      {selectedNode ? (
        renderNodeDetails ? (
          <div className="text-xs text-fg-dim">
            <NodeDetailsRenderer fn={renderNodeDetails} node={selectedNode} />
          </div>
        ) : (
          <div className="flex flex-col gap-2 text-xs">
            <div className="rounded-md border border-line bg-surface-2/50 px-2 py-1.5">
              <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-fg-mute">
                Label
              </div>
              <div className="text-fg">{nodeLabel(selectedNode)}</div>
            </div>
            <div className="rounded-md border border-line bg-surface-2/50 px-2 py-1.5">
              <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-fg-mute">
                Status
              </div>
              <div className="text-fg capitalize">
                {normalizeStatus(
                  getNodeStatus?.(selectedNode) ??
                    normalizeDevice(selectedNode).status,
                )}
              </div>
            </div>
            <div className="rounded-md border border-line bg-surface-2/50 px-2 py-1.5">
              <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-fg-mute">
                Model
              </div>
              <div className="text-fg">
                {normalizeDevice(selectedNode).model ?? "—"}
              </div>
            </div>
            {selectedNodeDetailsLoading ? (
              <div className="rounded-md border border-line bg-surface-2/40 px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-fg-mute">
                Loading device details...
              </div>
            ) : null}
            {selectedNodeDetailsError ? (
              <div className="rounded-md border border-rose/40 bg-rose/8 px-2 py-1.5 text-[11px] text-rose">
                {selectedNodeDetailsError}
              </div>
            ) : null}
            {selectedNodeDetails?.productType === "switch" &&
            selectedNodeDetails.ports?.length ? (
              <ExplorerPortsList ports={selectedNodeDetails.ports} />
            ) : null}
            {(selectedNodeDetails?.productType === "wireless" ||
              selectedNodeDetails?.productType === "appliance") &&
            selectedNodeDetails.clients?.length ? (
              <ExplorerClientsList clients={selectedNodeDetails.clients} />
            ) : (selectedNodeDetails?.productType === "wireless" ||
                selectedNodeDetails?.productType === "appliance") &&
              !selectedNodeDetailsLoading ? (
              <div className="rounded-md border border-line bg-surface-2/40 px-2 py-3 text-center text-[11px] text-fg-mute">
                No connected clients in the last hour.
              </div>
            ) : null}
            {!selectedNodeDetails &&
            !selectedNodeDetailsLoading &&
            !selectedNodeDetailsError ? (
              <div className="rounded-md border border-line bg-surface-2/40 px-2 py-3 text-center text-[11px] text-fg-mute">
                Select a node, then refresh if details are stale.
              </div>
            ) : null}
          </div>
        )
      ) : (
        <div className="rounded-md border border-line bg-surface-2/40 px-2 py-6 text-center text-xs text-fg-mute">
          Click a node to inspect details.
        </div>
      )}
    </div>
  );
}

type InteractionState = {
  selectedNodeId: string | null;
  hoverNodeId: string | null;
  pinnedNodeId: string | null;
  hoveredEdgeId: string | null;
  hoverCard: HoverCardState | null;
};

function useTopologyExplorer({
  nodes,
  edges,
  showAccessPointsByDefault,
  getNodeStatus,
  isAccessPointNode,
  onSelectedNodeChange,
  getNodeDetails,
  getNodeDetailsLoading,
  getNodeDetailsError,
}: {
  nodes: Node[];
  edges: Edge[];
  showAccessPointsByDefault: boolean;
  getNodeStatus?: (node: Node) => string | null | undefined;
  isAccessPointNode?: (node: Node) => boolean;
  onSelectedNodeChange?: (node: Node | null) => void;
  getNodeDetails?: (node: Node) => TopologyExplorerNodeDetails | undefined;
  getNodeDetailsLoading?: (node: Node) => boolean;
  getNodeDetailsError?: (node: Node) => string | undefined;
}) {
  const [enabledStatuses, setEnabledStatuses] = useState<Set<TopologyExplorerStatus>>(
    () => new Set(STATUS_ORDER),
  );
  const [showAccessPoints, setShowAccessPoints] = useState(showAccessPointsByDefault);
  const [
    { selectedNodeId, hoverNodeId, pinnedNodeId, hoveredEdgeId, hoverCard },
    setInteraction,
  ] = useReducer(
    (s: InteractionState, p: Partial<InteractionState>) => ({ ...s, ...p }),
    { selectedNodeId: null, hoverNodeId: null, pinnedNodeId: null, hoveredEdgeId: null, hoverCard: null },
  );
  const hostRef = useRef<HTMLDivElement | null>(null);

  const baseNodes = useMemo(
    () =>
      nodes.map((n) => {
        const device = normalizeDevice(n);
        return { ...n, data: { ...(n.data as object), device }, type: "default" } as Node;
      }),
    [nodes],
  );

  const accessPointCount = useMemo(() => {
    if (!isAccessPointNode) return 0;
    return nodes.filter((n) => isAccessPointNode(n)).length;
  }, [isAccessPointNode, nodes]);

  const filteredNodes = useMemo(() => {
    return baseNodes.filter((node) => {
      const device = normalizeDevice(node);
      const status = normalizeStatus(getNodeStatus?.(node) ?? device.status);
      if (!enabledStatuses.has(status)) return false;
      const label = nodeLabel(node);
      const isAp = isAccessPointNode
        ? isAccessPointNode(node)
        : inferIsAccessPoint(device, label);
      if (!showAccessPoints && isAp) return false;
      return true;
    });
  }, [baseNodes, enabledStatuses, getNodeStatus, isAccessPointNode, showAccessPoints]);

  const allowedIds = useMemo(
    () => new Set(filteredNodes.map((n) => n.id)),
    [filteredNodes],
  );

  const filteredEdges = useMemo(
    () => edges.filter((e) => allowedIds.has(e.source) && allowedIds.has(e.target)),
    [allowedIds, edges],
  );

  const selectedNode = useMemo(
    () => filteredNodes.find((n) => n.id === selectedNodeId) ?? null,
    [filteredNodes, selectedNodeId],
  );

  const selectedNodeDetails = useMemo(
    () => (selectedNode && getNodeDetails ? getNodeDetails(selectedNode) : undefined),
    [getNodeDetails, selectedNode],
  );

  const selectedNodeDetailsLoading = useMemo(
    () => (selectedNode && getNodeDetailsLoading ? getNodeDetailsLoading(selectedNode) : false),
    [getNodeDetailsLoading, selectedNode],
  );

  const selectedNodeDetailsError = useMemo(
    () => (selectedNode && getNodeDetailsError ? getNodeDetailsError(selectedNode) : undefined),
    [getNodeDetailsError, selectedNode],
  );

  const nodeById = useMemo(
    () => new Map(filteredNodes.map((n) => [n.id, n])),
    [filteredNodes],
  );

  const focusNodeId = hoverNodeId ?? pinnedNodeId;
  const edgeHasFocus = Boolean(focusNodeId || hoveredEdgeId);

  const styledEdges = useMemo<Edge[]>(() => {
    return filteredEdges.map((e) => {
      const sourceNode = nodeById.get(e.source);
      const sourceDevice = sourceNode ? normalizeDevice(sourceNode) : undefined;
      const data = (e.data as ExplorerEdgeData | undefined) ?? undefined;
      const incident = e.source === focusNodeId || e.target === focusNodeId;
      const sourceSwitchFocused =
        !!focusNodeId &&
        e.source === focusNodeId &&
        inferIsSwitch(
          sourceDevice ?? {},
          nodeLabel(sourceNode ?? ({ id: e.source, data: {} } as Node)),
        );
      const isHoveredEdge = hoveredEdgeId === e.id;
      const shouldShowPort = isHoveredEdge || sourceSwitchFocused;
      const focusedLabel = shouldShowPort
        ? data?.sourcePort || data?.targetPort || (typeof e.label === "string" ? e.label : null)
        : null;
      const base = e.style ?? {};
      const strokeOpacity = focusNodeId
        ? incident ? 0.95 : 0.07
        : isHoveredEdge ? 0.95 : base.strokeOpacity;
      const strokeWidth = focusNodeId
        ? incident ? 2.5 : 1
        : isHoveredEdge ? 2.5 : base.strokeWidth;
      return {
        ...e,
        label: focusedLabel ?? undefined,
        labelStyle: focusedLabel
          ? { fontSize: 10, fontFamily: "var(--font-mono, ui-monospace)", fill: "rgb(var(--fg))" }
          : undefined,
        labelBgPadding: focusedLabel ? [5, 3] : undefined,
        labelBgBorderRadius: focusedLabel ? 5 : undefined,
        labelBgStyle: focusedLabel
          ? { fill: "rgb(var(--bg-0))", fillOpacity: 0.9, stroke: "rgb(var(--line))", strokeOpacity: 0.85 }
          : undefined,
        zIndex: isHoveredEdge ? 14 : incident ? 10 : 0,
        animated: isHoveredEdge || incident ? e.animated : edgeHasFocus ? false : e.animated,
        style: { ...base, strokeOpacity, strokeWidth },
      };
    });
  }, [edgeHasFocus, filteredEdges, focusNodeId, hoveredEdgeId, nodeById]);

  function setHoverCardFromEvent(event: React.MouseEvent, nodeId: string) {
    const hostRect = hostRef.current?.getBoundingClientRect();
    if (!hostRect) return;
    const CARD_W = 300;
    const CARD_H = 240;
    const PAD = 12;
    const desiredX = event.clientX - hostRect.left + 14;
    const desiredY = event.clientY - hostRect.top + 14;
    setInteraction({
      hoverCard: {
        nodeId,
        x: Math.max(PAD, Math.min(desiredX, hostRect.width - CARD_W - PAD)),
        y: Math.max(PAD, Math.min(desiredY, hostRect.height - CARD_H - PAD)),
      },
    });
  }

  function handleSetEnabledStatuses(next: Set<TopologyExplorerStatus>) {
    const newAllowed = new Set(
      baseNodes.flatMap((node) => {
        const device = normalizeDevice(node);
        const status = normalizeStatus(getNodeStatus?.(node) ?? device.status);
        if (!next.has(status)) return [];
        const label = nodeLabel(node);
        const isAp = isAccessPointNode ? isAccessPointNode(node) : inferIsAccessPoint(device, label);
        return showAccessPoints || !isAp ? [node.id] : [];
      }),
    );
    if (selectedNodeId && !newAllowed.has(selectedNodeId)) {
      setInteraction({ selectedNodeId: null });
      onSelectedNodeChange?.(null);
    }
    if (hoverNodeId && !newAllowed.has(hoverNodeId)) setInteraction({ hoverNodeId: null });
    setEnabledStatuses(next);
  }

  function handleSetShowAccessPoints(next: boolean) {
    const newAllowed = new Set(
      baseNodes.flatMap((node) => {
        const device = normalizeDevice(node);
        const status = normalizeStatus(getNodeStatus?.(node) ?? device.status);
        if (!enabledStatuses.has(status)) return [];
        const label = nodeLabel(node);
        const isAp = isAccessPointNode ? isAccessPointNode(node) : inferIsAccessPoint(device, label);
        return next || !isAp ? [node.id] : [];
      }),
    );
    if (selectedNodeId && !newAllowed.has(selectedNodeId)) {
      setInteraction({ selectedNodeId: null });
      onSelectedNodeChange?.(null);
    }
    if (hoverNodeId && !newAllowed.has(hoverNodeId)) setInteraction({ hoverNodeId: null });
    setShowAccessPoints(next);
  }

  const hoverCardNode = hoverCard ? nodeById.get(hoverCard.nodeId) : null;
  const hoverDevice = hoverCardNode ? normalizeDevice(hoverCardNode) : null;

  return {
    enabledStatuses,
    showAccessPoints,
    hoverNodeId,
    selectedNode,
    hoverCard,
    hoverDevice,
    accessPointCount,
    filteredNodes,
    filteredEdges,
    styledEdges,
    selectedNodeDetails,
    selectedNodeDetailsLoading,
    selectedNodeDetailsError,
    hostRef,
    setInteraction,
    handleSetEnabledStatuses,
    handleSetShowAccessPoints,
    setHoverCardFromEvent,
  };
}

export function TopologyExplorer({
  nodes,
  edges,
  colorMode,
  fitDep,
  initialFocusNodeId,
  height = 420,
  emptyMessage = "No topology nodes available.",
  filterLabel = "Filters",
  showAccessPointsByDefault = true,
  getNodeStatus,
  isAccessPointNode,
  onSelectedNodeChange,
  onRefreshSelectedNode,
  getNodeDetails,
  getNodeDetailsLoading,
  getNodeDetailsError,
  renderNodeDetails,
}: {
  nodes: Node[];
  edges: Edge[];
  colorMode: "light" | "dark";
  fitDep: string;
  initialFocusNodeId?: string;
  height?: number;
  emptyMessage?: string;
  filterLabel?: string;
  showAccessPointsByDefault?: boolean;
  getNodeStatus?: (node: Node) => string | null | undefined;
  isAccessPointNode?: (node: Node) => boolean;
  onSelectedNodeChange?: (node: Node | null) => void;
  onRefreshSelectedNode?: (node: Node) => void;
  getNodeDetails?: (node: Node) => TopologyExplorerNodeDetails | undefined;
  getNodeDetailsLoading?: (node: Node) => boolean;
  getNodeDetailsError?: (node: Node) => string | undefined;
  renderNodeDetails?: (node: Node) => React.ReactNode;
}) {
  const {
    enabledStatuses,
    showAccessPoints,
    hoverNodeId,
    selectedNode,
    hoverCard,
    hoverDevice,
    accessPointCount,
    filteredNodes,
    filteredEdges,
    styledEdges,
    selectedNodeDetails,
    selectedNodeDetailsLoading,
    selectedNodeDetailsError,
    hostRef,
    setInteraction,
    handleSetEnabledStatuses,
    handleSetShowAccessPoints,
    setHoverCardFromEvent,
  } = useTopologyExplorer({
    nodes,
    edges,
    showAccessPointsByDefault,
    getNodeStatus,
    isAccessPointNode,
    onSelectedNodeChange,
    getNodeDetails,
    getNodeDetailsLoading,
    getNodeDetailsError,
  });

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_280px]">
      <div
        ref={hostRef}
        className="relative rounded-xl border border-line bg-surface p-2"
      >
        <TopologyFlowCanvas
          nodes={filteredNodes}
          edges={styledEdges}
          nodeTypes={NODE_TYPES}
          colorMode={colorMode}
          fitDep={`${fitDep}:${filteredNodes.length}:${filteredEdges.length}`}
          focusNodeId={initialFocusNodeId}
          height={height}
          emptyMessage={emptyMessage}
          onNodeClick={(_e, node) => {
            setInteraction({ selectedNodeId: node.id, pinnedNodeId: node.id });
            onSelectedNodeChange?.(node);
          }}
          onPaneClick={() => {
            setInteraction({ selectedNodeId: null, pinnedNodeId: null, hoveredEdgeId: null, hoverCard: null });
            onSelectedNodeChange?.(null);
          }}
          onNodeMouseEnter={(e, node) => {
            setInteraction({ hoverNodeId: node.id });
            setHoverCardFromEvent(e, node.id);
          }}
          onNodeMouseMove={(e, node) => {
            if (hoverNodeId === node.id) setHoverCardFromEvent(e, node.id);
          }}
          onNodeMouseLeave={() => {
            setInteraction({ hoverNodeId: null, hoverCard: null });
          }}
          onEdgeMouseEnter={(_e, edge) => setInteraction({ hoveredEdgeId: edge.id })}
          onEdgeMouseLeave={() => setInteraction({ hoveredEdgeId: null })}
        />

        <TopologyFilterOverlay
          filterLabel={filterLabel}
          enabledStatuses={enabledStatuses}
          setEnabledStatuses={handleSetEnabledStatuses}
          isAccessPointNode={isAccessPointNode}
          accessPointCount={accessPointCount}
          showAccessPoints={showAccessPoints}
          setShowAccessPoints={handleSetShowAccessPoints}
        />

        {hoverCard && hoverDevice ? (
          <TopologyHoverCard hoverCard={hoverCard} hoverDevice={hoverDevice} />
        ) : null}
      </div>

      <TopologyDetailsPanel
        selectedNode={selectedNode}
        renderNodeDetails={renderNodeDetails}
        onRefreshSelectedNode={onRefreshSelectedNode}
        onClose={() => { setInteraction({ selectedNodeId: null, pinnedNodeId: null }); onSelectedNodeChange?.(null); }}
        selectedNodeDetailsLoading={selectedNodeDetailsLoading}
        selectedNodeDetailsError={selectedNodeDetailsError}
        selectedNodeDetails={selectedNodeDetails}
        getNodeStatus={getNodeStatus}
      />
    </div>
  );
}

function ExplorerPortsList({ ports }: { ports: TopologyExplorerPort[] }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-line bg-surface-2/40 p-2">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.12em] text-fg-mute">
        <span>Ports</span>
        <span>
          {
            ports.filter((p) => (p.status ?? "").toLowerCase() === "connected")
              .length
          }
          /{ports.length}
        </span>
      </div>
      <div className="max-h-[210px] overflow-y-auto rounded-md border border-line/70">
        {ports.map((p) => {
          const connected = (p.status ?? "").toLowerCase() === "connected";
          return (
            <div
              key={p.portId ?? p.name ?? `${p.clientCount ?? 0}-${p.vlan ?? "no-vlan"}`}
              className="flex items-center gap-2 border-b border-line/60 px-2 py-1 text-[11px] last:border-b-0"
            >
              <span className="w-8 shrink-0 font-mono text-fg">
                {p.portId ?? "—"}
              </span>
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  connected ? "bg-mint" : "bg-fg-mute",
                )}
              />
              <span className="min-w-0 flex-1 truncate text-fg-dim">
                {p.name ?? "port"}
              </span>
              {typeof p.clientCount === "number" && p.clientCount > 0 ? (
                <span className="shrink-0 font-mono text-[10px] text-violet">
                  {p.clientCount} cli
                </span>
              ) : null}
              {p.speed ? (
                <span className="shrink-0 font-mono text-[10px] text-fg-mute">
                  {p.speed}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExplorerClientsList({
  clients,
}: {
  clients: TopologyExplorerClient[];
}) {
  const visible = clients.slice(0, 40);
  return (
    <div className="flex flex-col gap-1 rounded-md border border-line bg-surface-2/40 p-2">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.12em] text-fg-mute">
        <span>Clients</span>
        <span>{clients.length}</span>
      </div>
      <div className="max-h-[210px] overflow-y-auto rounded-md border border-line/70">
        {visible.map((c) => (
          <div
            key={c.id ?? c.mac ?? `${c.description ?? ""}-${c.ip ?? ""}`}
            className="flex items-center gap-2 border-b border-line/60 px-2 py-1 text-[11px] last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-fg">
                {c.description ?? c.mac ?? "—"}
              </div>
              <div className="font-mono text-[9.5px] text-fg-mute">
                {c.ip ?? "—"}
                {c.ssid
                  ? ` · ${c.ssid}`
                  : c.switchport
                    ? ` · port ${c.switchport}`
                    : ""}
                {c.vlan ? ` · vlan ${c.vlan}` : ""}
              </div>
            </div>
            {c.manufacturer ? (
              <span className="shrink-0 font-mono text-[9.5px] text-fg-mute">
                {c.manufacturer}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function HoverDetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-line/70 bg-surface/55 px-2 py-1">
      <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-fg-mute">
        {label}
      </span>
      <span
        className={cn(
          "max-w-[58%] truncate text-right text-fg",
          mono && "font-mono",
        )}
      >
        {value}
      </span>
    </div>
  );
}
